import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Mail, Lock, User } from "lucide-react";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        const res = await register(email, password, name);
        setBusy(false);
        if (res.ok) nav("/dashboard");
        else setError(res.error);
    };

    return (
        <div className="grid min-h-screen md:grid-cols-2">
            <div className="flex flex-col items-center justify-center bg-sand px-6 py-12 order-2 md:order-1">
                <div className="w-full max-w-sm">
                    <Logo />
                    <h1 className="mt-10 font-display text-4xl text-ink">Start exploring.</h1>
                    <p className="mt-2 text-moss">Save trips, hotels and favourite spots.</p>

                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
                        <label className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-jade/15 focus-within:ring-terracotta/40">
                            <User size={16} className="text-jade" />
                            <input
                                data-testid="register-name"
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your name"
                                className="w-full bg-transparent outline-none"
                            />
                        </label>
                        <label className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-jade/15 focus-within:ring-terracotta/40">
                            <Mail size={16} className="text-jade" />
                            <input
                                data-testid="register-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@email.com"
                                className="w-full bg-transparent outline-none"
                            />
                        </label>
                        <label className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-jade/15 focus-within:ring-terracotta/40">
                            <Lock size={16} className="text-jade" />
                            <input
                                data-testid="register-password"
                                type="password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="At least 6 characters"
                                className="w-full bg-transparent outline-none"
                            />
                        </label>
                        {error && (
                            <p data-testid="register-error" className="rounded-2xl bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</p>
                        )}
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="register-submit"
                            className="btn-primary w-full justify-center !py-4"
                        >
                            {busy ? "Creating…" : "Create my account"}
                        </button>
                    </form>

                    <p className="mt-6 text-sm text-moss">
                        Already have an account?{" "}
                        <Link to="/login" data-testid="register-go-login" className="text-terracotta hover:underline">Sign in</Link>
                    </p>
                </div>
            </div>
            <div className="relative hidden md:block order-1 md:order-2">
                <img
                    src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1400"
                    alt="Travel"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-jade/40 to-ink/40" />
                <div className="absolute bottom-10 left-10 right-10 text-sand">
                    <p className="overline text-sand/80">join werighty</p>
                    <p className="mt-3 font-display text-4xl leading-tight">Every great journey starts with a question. Yours starts here.</p>
                </div>
            </div>
        </div>
    );
}
